/**
 * 来場者ログインフォームフック
 * 入力検証を含むログインUI状態を管理します
 */

import { useCallback, useState } from 'react';
import {
  VISITOR_LOGIN_CHECK_ERROR_MESSAGE,
  VISITOR_LOGIN_FAILED_ERROR_MESSAGE,
  VISITOR_LOGIN_INVALID_CREDENTIALS_ERROR_MESSAGE,
  VISITOR_LOGIN_RATE_LIMIT_ERROR_MESSAGE,
} from '../constants';
import {
  visitorLogin,
  VISITOR_LOGIN_INVALID_CREDENTIALS_CODE,
  VISITOR_LOGIN_INVALID_INPUT_CODE,
  VISITOR_LOGIN_RATE_LIMIT_CODE,
} from '../services/visitorAuthService';
import { saveVisitorSession } from '../services/visitorSessionService';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';
import {
  VISITOR_LOGIN_USERNAME_MAX_LENGTH,
  truncateVisitorUsername,
  validateVisitorLoginUsername,
  validateVisitorPassword,
} from '../../../shared/utils/validation';

/**
 * ログイン送信結果を生成する
 * @param {boolean} isSuccess - 成功有無
 * @param {Object} [payload] - 送信成功時のペイロード
 * @param {'username' | 'password'} [invalidField] - 最初の不正入力
 * @returns {{
 * isSuccess: boolean,
 * payload?: { visitorId: string, nickname: string, currentPoint: number, maxReachedPoint: number, sessionToken: string, sessionExpiresAt: string },
 * invalidField?: 'username' | 'password',
 * }} 送信結果
 */
const createSubmitResult = (
  isSuccess,
  payload = undefined,
  invalidField = undefined,
) => {
  return {
    isSuccess,
    payload,
    invalidField,
  };
};

/**
 * 来場者ログインフォーム状態を返す
 * @returns {{
 * username: string,
 * password: string,
 * errorMessage: string,
 * isSubmitting: boolean,
 * handleUsernameChange: (value: string) => void,
 * handlePasswordChange: (value: string) => void,
 * submitLogin: () => Promise<{
 * isSuccess: boolean,
 * payload?: { visitorId: string, nickname: string, currentPoint: number, maxReachedPoint: number, sessionToken: string, sessionExpiresAt: string },
 * }>,
 * }}
 */
export const useVisitorLoginForm = () => {
  /** ユーザ名入力値 */
  const [username, setUsername] = useState('');

  /** パスワード入力値 */
  const [password, setPassword] = useState('');

  /** 画面表示エラーメッセージ */
  const [errorMessage, setErrorMessage] = useState('');

  /** 送信中状態 */
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * ユーザ名入力変更時の処理
   * @param {string} value - 入力値
   * @returns {void}
   */
  const handleUsernameChange = useCallback((value) => {
    setUsername(
      truncateVisitorUsername(value, VISITOR_LOGIN_USERNAME_MAX_LENGTH),
    );
    setErrorMessage('');
  }, []);

  /**
   * パスワード入力変更時の処理
   * @param {string} value - 入力値
   * @returns {void}
   */
  const handlePasswordChange = useCallback((value) => {
    setPassword(value);
    setErrorMessage('');
  }, []);

  /**
   * ログイン送信処理
   * @returns {Promise<{
   * isSuccess: boolean,
   * payload?: { visitorId: string, nickname: string, currentPoint: number, maxReachedPoint: number, sessionToken: string, sessionExpiresAt: string },
   * }>} 送信結果
   */
  const submitLogin = useCallback(async () => {
    /** ユーザ名検証結果 */
    const usernameValidationResult = validateVisitorLoginUsername(username);

    if (!usernameValidationResult.isValid) {
      setErrorMessage(usernameValidationResult.message);
      return createSubmitResult(false, undefined, 'username');
    }

    /** パスワード検証結果 */
    const passwordValidationResult = validateVisitorPassword(password);

    if (!passwordValidationResult.isValid) {
      setErrorMessage(passwordValidationResult.message);
      return createSubmitResult(false, undefined, 'password');
    }

    setIsSubmitting(true);

    try {
      /** ログイン結果 */
      const loginResult = await visitorLogin(username, password);
      await saveVisitorSession({
        visitorId: loginResult.visitorId,
        nickname: loginResult.nickname,
        sessionToken: loginResult.sessionToken,
        sessionExpiresAt: loginResult.sessionExpiresAt,
      });

      setErrorMessage('');
      return createSubmitResult(true, loginResult);
    } catch (error) {
      if (error instanceof AppError) {
        if (error.code === VISITOR_LOGIN_INVALID_INPUT_CODE) {
          setErrorMessage(error.message);
          return createSubmitResult(false);
        }

        if (error.code === VISITOR_LOGIN_INVALID_CREDENTIALS_CODE) {
          setErrorMessage(VISITOR_LOGIN_INVALID_CREDENTIALS_ERROR_MESSAGE);
          return createSubmitResult(false);
        }

        if (error.code === VISITOR_LOGIN_RATE_LIMIT_CODE) {
          setErrorMessage(VISITOR_LOGIN_RATE_LIMIT_ERROR_MESSAGE);
          return createSubmitResult(false);
        }
      }

      logError('来場者ログイン送信処理に失敗しました。', {
        username,
        errorMessage: error instanceof Error ? error.message : String(error),
      });

      // RPCが失敗した場合は通信起因を優先表示し、原因不明時のみ一般エラーを表示する
      const fallbackMessage =
        error instanceof AppError ? VISITOR_LOGIN_CHECK_ERROR_MESSAGE : VISITOR_LOGIN_FAILED_ERROR_MESSAGE;
      setErrorMessage(fallbackMessage);

      return createSubmitResult(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [password, username]);

  return {
    username,
    password,
    errorMessage,
    isSubmitting,
    handleUsernameChange,
    handlePasswordChange,
    submitLogin,
  };
};
